import * as React from 'react';
import {useRef} from 'react';
import type {Strategy as FloatingStrategy} from '@floating-ui/react';
import type {Placement} from '@react-types/overlays';
import {
  assignProps,
  createViewConfig,
  view,
  toIntent,
  scheduleStyleIntent,
} from '@sail/engine';
import {useId, useFocusVisible, mergeRefs} from '@sail/react-aria';
import {tokens as $} from '@sail/tokens';
import {useFloating} from './popover/useFloating';
import {css} from '../css';
import {Layer} from './layers/Layer';
import type {View} from '../view';
import {toFloatingPlacement} from './utils/toFloatingPlacement';
import {useTooltipIframe} from '../iframe-overrides/TooltipOverrides';
import {useUiConfig} from '../providers/UiConfigProvider';

const DEFAULT_OPEN_DELAY = 250;
const DEFAULT_CLOSE_DELAY = 500;
const MINIMUM_DURATION = 800;

const tooltipLayerIframeOverrides = toIntent([
  assignProps(useTooltipIframe),
  // We "hijack" the overlayRef prop on the TooltipLayer as we want to
  // assign the overlayRef to the iframe that we created
  assignProps({overlayRef: {current: null}}),
]);

const triggerIsolationClassName = 'tooltip-trigger-isolate';

scheduleStyleIntent(
  css.global({
    [`.${triggerIsolationClassName}`]: {
      isolation: 'isolate',
    },
  }),
);

type DelayOptions = {show?: number; hide?: number};

export type TooltipProps = View.IntrinsicElement<
  'div',
  {
    /**
     * The target element that should be used as the Tooltip trigger.
     * @external
     */
    trigger: React.ReactElement | React.RefObject<HTMLElement>;
    /**
     * How the tooltip should be positioned relative to the trigger element.
     * @external
     * */
    floatingStrategy?: FloatingStrategy;
    /**
     * How the Tooltip should be placed relative to the trigger element.
     * @external
     */
    placement: Placement;
    /**
     * Array of placements to try if the initial placement doesn't fit on the aces in which overflow is checked.
     *
     * If `placement` is set to `'top'` and `fallbackPlacements` is set to `['bottom', 'left']`, the placements to try
     * (in order) are `['top', 'bottom', 'left']`.
     * @external
     */
    fallbackPlacements?: Placement[];

    /**
     * How much time in milliseconds to wait before showing the Tooltip on hover.
     * @external
     */
    delay?: number | DelayOptions;

    /**
     * The style variant of the Tooltip.
     * @external
     */
    type?: 'description' | 'label';

    /**
     * The content to place inside of the Tooltip.
     * @external
     */
    children?: React.ReactNode;

    /** The available subviews */
    subviews?: View.Subviews<{layer: typeof Layer}>;
  }
>;

type TooltipStateControl = (options?: {immediate?: boolean}) => void;

type TooltipCommand = 'open' | 'close';
type TooltipInterval = 'warmup' | 'cooldown' | 'minimum';
type PendingAction = {
  command: TooltipCommand;
  after: TooltipInterval;
};
type TooltipAction = TooltipCommand | PendingAction;

type TooltipStatus = 'open' | 'closed';

type TooltipState = {
  status: TooltipStatus;
  pending: PendingAction | null;
  openedAt: number | null;
};

function tooltipStateReducer(
  state: TooltipState,
  action: TooltipAction,
): TooltipState {
  switch (action) {
    case 'open':
      return {
        status: 'open',
        pending: null,
        openedAt: Date.now(),
      };
    case 'close':
      return {
        ...state,
        status: 'closed',
        pending: null,
        openedAt: null,
      };
    default: {
      return {
        ...state,
        pending: action,
      };
    }
  }
}

const getOpenDelay = (delay: TooltipProps['delay']) => {
  if (typeof delay === 'number') {
    return delay;
  }
  return delay?.show ?? DEFAULT_OPEN_DELAY;
};

const getCloseDelay = (delay: TooltipProps['delay']) => {
  if (typeof delay === 'number') {
    return DEFAULT_CLOSE_DELAY;
  }
  return delay?.hide ?? DEFAULT_CLOSE_DELAY;
};

// animation.duration.long token is 300ms + 50ms of buffer
const TRANSITION_TIMEOUT = 350;

const useTooltipAnimation = (isOpen: boolean) => {
  const [visible, setVisible] = React.useState(false);
  const uiConfig = useUiConfig();
  const shouldApplyIframeOverrides = !!uiConfig?._unstable_requestIframe;
  const transitionTimeoutRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (isOpen && !visible) {
      setVisible(true);
    } else if (!isOpen && visible) {
      transitionTimeoutRef.current = window.setTimeout(() => {
        setVisible(false);

        transitionTimeoutRef.current = null;
      }, TRANSITION_TIMEOUT);

      return () => {
        if (transitionTimeoutRef.current !== null) {
          window.clearTimeout(transitionTimeoutRef.current);
          transitionTimeoutRef.current = null;
        }
      };
    }
  }, [isOpen, visible]);

  if (process.env.NODE_ENV === 'test') {
    return {
      shouldRender: isOpen,
      animate: css({opacity: 1}),
    };
  }

  // we need 0.999 to trigger onTransitionEnd in the iframe overrides
  const baseOpacity = shouldApplyIframeOverrides ? 0.999 : 0;

  return {
    shouldRender: isOpen || visible,
    animate: toIntent([
      css({
        opacity: baseOpacity,
        transitionProperty: 'opacity',
        transitionDuration: $['animation.duration.long'],
        transitionTimingFunction: $['animation.easing'],
        '@media (prefers-reduced-motion: reduce)': {
          transitionDuration: 'instant',
          transitionTimingFunction: 'instant',
        },
      }),
      visible && isOpen
        ? css({
            opacity: 1,
            transitionDuration: $['animation.duration.short'],
          })
        : css({
            opacity: baseOpacity,
            transitionDuration: $['animation.duration.long'],
          }),
      assignProps({
        onTransitionEnd: () => {
          if (!isOpen) {
            if (transitionTimeoutRef.current !== null) {
              window.clearTimeout(transitionTimeoutRef.current);
              transitionTimeoutRef.current = null;
            }

            setVisible(false);
          }
        },
      }),
    ]),
  };
};

type UseTooltipStateOptions = {
  id: string;
  delay: TooltipProps['delay'];
};

const tooltips: Record<string, TooltipStateControl> = {};

const tooltipRegistry = {
  register(id: string, closeCallback: TooltipStateControl) {
    tooltips[id] = closeCallback;
  },
  unregister(id: string) {
    delete tooltips[id];
  },
  closeOtherTooltips(exceptId: string) {
    Object.keys(tooltips).forEach((id) => {
      if (id !== exceptId) {
        tooltips[id]();
      }
    });
  },
  hasActiveTooltips() {
    return Object.keys(tooltips).length > 0;
  },
};

function useTooltipState({id, delay}: UseTooltipStateOptions) {
  const {isFocusVisible} = useFocusVisible();

  const [state, dispatch] = React.useReducer(tooltipStateReducer, {
    status: 'closed',
    pending: null,
    openedAt: null,
  });

  const openDelay = getOpenDelay(delay);
  const closeDelay = getCloseDelay(delay);

  const open = React.useCallback<TooltipStateControl>(
    ({immediate = false} = {}) => {
      dispatch(
        immediate || openDelay <= 0 || tooltipRegistry.hasActiveTooltips()
          ? 'open'
          : {command: 'open', after: 'warmup'},
      );
    },
    [openDelay],
  );

  const close = React.useCallback<TooltipStateControl>(
    ({immediate = false} = {}) => {
      if (isFocusVisible) {
        return dispatch('close');
      }

      dispatch({
        command: 'close',
        after: immediate ? 'minimum' : 'cooldown',
      });
    },
    [isFocusVisible],
  );

  React.useEffect(() => {
    if (state.status === 'open') {
      tooltipRegistry.closeOtherTooltips(id);
      tooltipRegistry.register(id, () => dispatch('close'));
    }

    if (state.status === 'closed') {
      tooltipRegistry.unregister(id);
    }
  }, [id, state.status]);

  React.useEffect(() => {
    if (state.pending?.command === 'open') {
      const timeout = window.setTimeout(() => {
        dispatch('open');
      }, openDelay);

      return () => {
        window.clearTimeout(timeout);
      };
    }

    if (state.pending?.command === 'close') {
      const after = state.pending.after;

      const uptime = state.openedAt ? Date.now() - state.openedAt : 0;
      const closeAt =
        after === 'minimum' ? MINIMUM_DURATION - uptime : closeDelay;

      if (closeAt <= 0) {
        dispatch('close');
      } else {
        const timeout = window.setTimeout(() => {
          dispatch('close');
        }, closeAt);

        return () => {
          window.clearTimeout(timeout);
        };
      }
    }
  }, [openDelay, closeDelay, state, isFocusVisible]);

  return {
    isOpen: state.status === 'open',
    open,
    close,
  };
}

export type TooltipLayerProps = View.IntrinsicElement<
  'div',
  {
    placement: Placement;
    type: TooltipProps['type'];
    floatingStrategy?: FloatingStrategy;
    fallbackPlacements?: Placement[];
    overlayRef: React.RefObject<HTMLDivElement>;
    triggerRef: React.RefObject<HTMLElement>;
    subviews?: View.Subviews<{layer: typeof Layer}>;
  }
>;

// This config is used by submerchant-surfaces IframeLayerOverrides enabling them
// to switch out the contents of the positioned tooltip with iframed content
// and providing them access to the reference and overlay elements.
export const TooltipLayerConfig = createViewConfig({
  name: 'TooltipLayer',
  props: {} as TooltipLayerProps,
});

const TooltipLayer = TooltipLayerConfig.createView(
  ({
    id,
    placement,
    floatingStrategy,
    type,
    overlayRef,
    triggerRef,
    children,
    subviews,
    fallbackPlacements,
    ...props
  }: View.RenderProps<TooltipLayerProps>) => {
    const padding = 4;
    const floatingPlacement = toFloatingPlacement(placement);
    const fallbackFloatingPlacements = fallbackPlacements?.map((placement) =>
      toFloatingPlacement(placement),
    );
    const {refs, floatingStyles, middlewareData} = useFloating<HTMLDivElement>({
      placement: floatingPlacement,
      floatingStrategy,
      offsetValue: padding,
      flipEnabled: true,
      fallbackPlacements: fallbackFloatingPlacements,
      hideOptions: {referenceHidden: true},
      shiftEnabled: true,
      shiftOptions: {padding},
    });

    const referenceHidden = middlewareData.hide?.referenceHidden;

    return (
      <Layer
        inherits={subviews.layer}
        ref={(el: HTMLDivElement) => {
          // don't run positioning logic within JSDOM environment (speeds up tests)
          // JSDOM doesn't support `getBoundingClientRect` anyway, so positioning logic
          // wouldn't be correct there anyway.
          if (typeof jest === 'undefined') {
            refs.setFloating(el);
            refs.setReference(triggerRef.current);
          }
        }}
        style={floatingStyles}
        uses={[assignProps({ref: overlayRef})]}
      >
        <view.div
          {...props}
          id={id}
          role="tooltip"
          // hide the tooltip if the reference element is not visible
          css={[
            referenceHidden
              ? css({visibility: 'hidden'})
              : css({visibility: 'visible'}),
          ]}
        >
          {children}
        </view.div>
      </Layer>
    );
  },
);

export const TooltipConfig = createViewConfig({
  props: {} as TooltipProps,
  name: 'Tooltip',
  defaults: {
    type: 'description',
    placement: 'bottom',
  },
});

/**
 * @external
 */
export const Tooltip = TooltipConfig.createView(
  ({
    trigger,
    placement,
    floatingStrategy,
    type,
    delay = DEFAULT_OPEN_DELAY,
    children,
    fallbackPlacements,
    ...props
  }) => {
    if (
      React.isValidElement(trigger) &&
      (trigger.type as unknown as symbol) === Symbol.for('react.fragment') &&
      process.env.NODE_ENV !== 'production'
    ) {
      throw new Error(
        'React fragments are not supported as Sail Tooltip triggers.',
      );
    }

    const id = useId();
    const {isFocusVisible} = useFocusVisible();
    const triggerRef = React.useRef<HTMLElement | null>(null);
    const overlayRef = React.useRef<HTMLDivElement>(null);
    const uiConfig = useUiConfig();
    const shouldApplyIframeOverrides = !!uiConfig?._unstable_requestIframe;

    React.useEffect(() => {
      if ('current' in trigger) {
        triggerRef.current = trigger.current;
        if (triggerRef.current) {
          // ensure the tooltip trigger creates a new stacking context
          // this enables the tooltip trigger to be compatible with Sail's `List` component
          triggerRef.current.classList.add(triggerIsolationClassName);
        }
      }
    }, [trigger]);

    const {
      isOpen,
      open: showTooltip,
      close: hideTooltip,
    } = useTooltipState({id, delay});
    const {shouldRender, animate} = useTooltipAnimation(isOpen);
    const position = useRef({clientX: 0, clientY: 0});
    const closeDelay = getCloseDelay(delay);

    React.useEffect(() => {
      const triggerElement = triggerRef.current;
      const overlayElement = overlayRef.current;

      function onFocus() {
        if (isFocusVisible) {
          showTooltip({immediate: true});
        }
      }

      function onBlur() {
        hideTooltip({immediate: true});
      }

      function onMouseEnter(e: MouseEvent) {
        position.current.clientX = e.clientX;
        position.current.clientY = e.clientY;
        showTooltip();
      }

      function onMouseLeave(e: MouseEvent) {
        if (
          // if mouse has remained in the same position since opening, then stay open
          position.current.clientX !== e.clientX ||
          position.current.clientY !== e.clientY ||
          // if mouse has left the window, then close
          (e.clientY === 0 && e.clientX === 0)
        ) {
          hideTooltip();
        }
      }

      if (triggerElement) {
        triggerElement.addEventListener('focus', onFocus);
        triggerElement.addEventListener('blur', onBlur);
        triggerElement.addEventListener('mouseenter', onMouseEnter);
        triggerElement.addEventListener('mouseleave', onMouseLeave);
      }

      if (overlayElement) {
        if (closeDelay > 0) {
          overlayElement.addEventListener('focus', onFocus);
          overlayElement.addEventListener('blur', onBlur);
          overlayElement.addEventListener('mouseenter', onMouseEnter);
        }
        // in the edge case where the overlay intersects the pointer when it opens,
        // we need to make sure that the tooltip closes when the pointer leaves the overlay
        overlayElement.addEventListener('mouseleave', onMouseLeave);
      }

      if (isOpen) {
        document.body.addEventListener('scroll', onBlur, {capture: true});
      }

      return () => {
        if (triggerElement) {
          triggerElement.removeEventListener('focus', onFocus);
          triggerElement.removeEventListener('blur', onBlur);
          triggerElement.removeEventListener('mouseenter', onMouseEnter);
          triggerElement.removeEventListener('mouseleave', onMouseLeave);
        }

        if (overlayElement) {
          if (type === 'description') {
            overlayElement.removeEventListener('focus', onFocus);
            overlayElement.removeEventListener('blur', onBlur);
            overlayElement.removeEventListener('mouseenter', onMouseEnter);
          }
          overlayElement.removeEventListener('mouseleave', onMouseLeave);
        }

        if (isOpen) {
          document.body.removeEventListener('scroll', onBlur, {
            capture: true,
          });
        }
      };
    }, [
      triggerRef,
      overlayRef,
      showTooltip,
      hideTooltip,
      isOpen,
      type,
      isFocusVisible,
      closeDelay,
    ]);

    React.useEffect(() => {
      function onKeyDown(event: KeyboardEvent) {
        if (event.key === 'Escape') {
          event.preventDefault();
          hideTooltip({immediate: true});
        }
      }

      if (isOpen) {
        window.addEventListener('keydown', onKeyDown);

        return () => {
          window.removeEventListener('keydown', onKeyDown);
        };
      }
    }, [isOpen, hideTooltip]);

    React.useEffect(() => {
      const triggerElement = triggerRef.current;

      if (!triggerElement?.getAttribute('tabindex')) {
        triggerElement?.setAttribute('tabindex', '0');
      }

      if (isOpen) {
        const attribute =
          type === 'description' ? 'aria-describedby' : 'aria-labelledby';

        triggerElement?.setAttribute(attribute, id);

        return () => {
          triggerElement?.removeAttribute(attribute);
        };
      }
    }, [isOpen, id, type]);

    return (
      <>
        {React.isValidElement<{
          ref: React.ForwardedRef<HTMLElement | null> | undefined;
          className?: string;
        }>(trigger) &&
          React.cloneElement(trigger, {
            ref: mergeRefs<HTMLElement | null>(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (trigger as any).ref,
              triggerRef,
            ),
            className: `${
              trigger.props.className ?? ''
            } ${triggerIsolationClassName}`.trim(),
          })}

        {shouldRender && React.Children.count(children) > 0 ? (
          <TooltipLayer
            {...props}
            id={id}
            type={type}
            floatingStrategy={floatingStrategy}
            placement={placement}
            fallbackPlacements={fallbackPlacements}
            overlayRef={overlayRef}
            triggerRef={triggerRef}
            uses={[
              ...(shouldApplyIframeOverrides
                ? tooltipLayerIframeOverrides
                : []),
              animate,
            ]}
          >
            {children}
          </TooltipLayer>
        ) : null}
      </>
    );
  },
  {
    css: {
      font: 'body.small',
      boxShadow: 'small',
    },
    forward: {type: true},
    variants: {
      type: {
        description: {
          css: {
            padding: 'medium',
            maxWidth: '300px',
            backgroundColor: 'surface',
            textColor: 'text',
            border: '1px solid',
            borderColor: 'border',
            borderRadius: 'medium',
          },
        },
        label: {
          css: {
            paddingX: 'small',
            paddingY: 'xsmall',
            maxWidth: '200px',
            textColor: $['color.neutral.0'],
            backgroundColor: $['color.neutral.700'],
            borderRadius: 'small',
          },
        },
      },
    },
  },
);
